import { createStore, applyMiddleware, compose, Middleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import RootReducer from './rootReducer';
import RootSaga from './rootSaga';
import { setStoreRef } from '../services/storeRef';

// Middlewares custom
import signalRMiddleware from '../middlewares/signalRMiddleware';

// States
import { UserState } from './user';
import { MenuState } from './menu';
import { CountryState } from './country';
import { StatusRegisterState } from './statusRegister';
import { CompanyTypeState } from './companyType';
import { CompanyState } from './company';
import { CompanyBranchState } from './companyBranch';
import { BodyshopState } from './bodyshop';
import { BodyshopBranchState } from './bodyshopBranch';
import { InsurerState } from './insurer';
import { InsuranceSublineState } from './insuranceSubline';
import { SupplierState } from './supplier';
import { ModalState } from './modal';
import { StatesState } from './state';
import { TranslateState } from './translate';
import { CultureState } from './culture';
import { UserGroupState } from './userGroup';
import { TimeZoneState } from './timeZone';
import { ContactTypeState } from './contactType';
import { MunicipalityState } from './municipality';
import { AddressTypeState } from './addressType';
import { QuoteStatusState } from './quoteStatus';
import { SupplierTypeState } from './supplierType';
import { SupplierCategoryState } from './supplierCategory';
import { YearState } from './vehicle/year';
import { ManufacturerState } from './vehicle/manufacturer';
import { ModelState } from './vehicle/model';
import { SubModelState } from './vehicle/subModel';
import { DamageAreaState } from './damageArea';
import { AddressByBodyshopState } from './addressByBodyshop';
import { CartState } from './cart';
import { PartTypeState } from './partType';
import { ControlState } from './control';
import { CategoryState } from './category';
import { ClaimTypeState } from './claimType';
import { ReasonState } from './reason';
import { DistributionRegionState } from './distributionRegion';
import { QuoteCountStatusState } from './quoteCountStatus';
import { SummaryState } from './summary';
import { CartStatusState } from './cartStatus';
import { CartCountStatusState } from './cartCountStatus';
import { BillingAddressState } from './billingAddress';
import { WarehouseState } from './warehouse';
import { BrandState } from './brand';
import { PackageState } from './package';
import { SupplierInventoryImportStatusState } from './supplierInventoryImportStatus';
import { SupplierInventoryWarehouseStatusState } from './supplierInventoryWarehouseStatus';
import { SubcategoryState } from './subcategory';
import { PositionState } from './position';
import { ParameterTypeValueState } from './parameterTypeValue';
import { DragDropScreenState } from './dragDropScreen/dragDropScreenTypes';
import { QuoteImportStatusState } from './quoteImportStatus/quoteImportStatusTypes';
import { IntegrationTriggerState } from './integrationTrigger';
import { IntegrationState } from './integrationType';
import { IntegrationQueueStatusState } from './integrationQueueStatus';
import { QueryParamsState } from './queryParams';
import { MappingTypeState } from './mappingType';
import { AssessmentTypeState } from './assessmentType';
import { ManagePackagesCountStatusState } from './managePackagesCountStatus';
import { ParameterByTypeScreenState } from './parameterByTypeScreen';
import { SignalRState } from './signalR';
import { AddressState } from './address';
import { ReportScheduleStatusState } from './reportScheduleStatus';
import { SchedulerUsersState } from './schedulerUsers';
import { UserTypeState } from './userType';
import { DeliveryTypeState } from './deliveryType';
import { PreQuoteTypeState } from './preQuoteType';
import { SystemApplicationState } from './systemApplication';
import { ValuatorState } from './valuator/valuatorTypes';
import { SupervisorState } from './supervisor';
import { ParameterState } from './parameter';
import { DamageCategoryState } from './damageCategory/damageCategoryTypes';
import { BodyshopTypeState } from './bodyshopType';
import { EntityTypeState } from './entityType';
import { CompanyManufacturerState } from './companyManufacturer';
import { AffectedTypeState } from './affectedType';
import { RateImportStatusState } from './rateImportStatus';
import { RequestFormatState } from './RequestFormat/requestFormatTypes';
import { ReportTemplateState } from './reportTemplate';
import { ReportTemplateTypeState } from './reportTemplateType';
import { ClaimFileCategoryState } from './claimFileCategory';
import { ExcludeVariablesState } from './excludeVariables';
import { VisualIdentityState } from './visualIdentity';
import { TemplateEmailNotificationState } from './templateEmailNotification/templateEmailNotificationTypes';
import { AnnouncementState } from './announcement';
import { AccessErrorState } from './accessError';
import { PredefinedSearchState } from './predefinedSearch';
import { DomainUsersByCompanyState } from './domainUsersByCompany';
import { PartBrandState } from '@/main/store/partBrand';
import { FileTypeState } from '@/main/store/fileType/fileTypeTypes';
import { AutomaticActionState } from '@/main/store/automaticAction';
import { ApprovalMatrixTypeState } from '@/main/store/approvalMatrixType';
import { UserHierarchyState } from '@/main/store/userHierarchy';

export interface ApplicationState {
  user: UserState;
  menu: MenuState;
  country: CountryState;
  culture: CultureState;
  statusRegister: StatusRegisterState;
  companyType: CompanyTypeState;
  company: CompanyState;
  companyBranch: CompanyBranchState;
  bodyshop: BodyshopState;
  bodyshopBranch: BodyshopBranchState;
  insurer: InsurerState;
  insuranceSubline: InsuranceSublineState;
  supplier: SupplierState;
  modal: ModalState;
  stateCountry: StatesState;
  translate: TranslateState;
  userGroup: UserGroupState;
  timeZone: TimeZoneState;
  contactType: ContactTypeState;
  municipality: MunicipalityState;
  addressType: AddressTypeState;
  quoteStatus: QuoteStatusState;
  supplierType: SupplierTypeState;
  supplierCategory: SupplierCategoryState;
  year: YearState;
  manufacturer: ManufacturerState;
  model: ModelState;
  subModel: SubModelState;
  damageArea: DamageAreaState;
  deliveryType: DeliveryTypeState;
  addressByBodyshop: AddressByBodyshopState;
  cart: CartState;
  partType: PartTypeState;
  control: ControlState;
  category: CategoryState;
  claimType: ClaimTypeState;
  reason: ReasonState;
  distributionRegion: DistributionRegionState;
  quoteCountStatus: QuoteCountStatusState;
  summary: SummaryState;
  cartStatus: CartStatusState;
  cartCountStatus: CartCountStatusState;
  billingAddress: BillingAddressState;
  warehouse: WarehouseState;
  brand: BrandState;
  package: PackageState;
  supplierInventoryImportStatus: SupplierInventoryImportStatusState;
  supplierInventoryWarehouseStatus: SupplierInventoryWarehouseStatusState;
  subcategory: SubcategoryState;
  position: PositionState;
  parameterTypeValue: ParameterTypeValueState;
  dragDropScreen: DragDropScreenState;
  quoteImportStatus: QuoteImportStatusState;
  integrationTrigger: IntegrationTriggerState;
  integration: IntegrationState;
  integrationQueueStatus: IntegrationQueueStatusState;
  queryParams: QueryParamsState;
  mappingType: MappingTypeState;
  assessmentType: AssessmentTypeState;
  managePackagesCountStatus: ManagePackagesCountStatusState;
  parameterByTypeScreen: ParameterByTypeScreenState;
  signalR: SignalRState;
  address: AddressState;
  reportScheduleStatus: ReportScheduleStatusState;
  schedulerUsers: SchedulerUsersState;
  userType: UserTypeState;
  deliveryTypes: DeliveryTypeState;
  preQuoteType: PreQuoteTypeState;
  systemApplication: SystemApplicationState;
  valuator: ValuatorState;
  supervisor: SupervisorState;
  parameter: ParameterState;
  damageCategory: DamageCategoryState;
  bodyshopType: BodyshopTypeState;
  entityType: EntityTypeState;
  companyManufacturer: CompanyManufacturerState;
  affectedType: AffectedTypeState;
  rateImportStatus: RateImportStatusState;
  requestFormat: RequestFormatState;
  reportTemplate: ReportTemplateState;
  reportTemplateType: ReportTemplateTypeState;
  claimFileCategory: ClaimFileCategoryState;
  excludeVariables: ExcludeVariablesState;
  visualIdentity: VisualIdentityState;
  templateEmailNotification: TemplateEmailNotificationState;
  announcement: AnnouncementState;
  accessError: AccessErrorState;
  predefinedSearch: PredefinedSearchState;
  domainUsersByCompany: DomainUsersByCompanyState;
  partBrand : PartBrandState,
  fileType: FileTypeState
  automaticAction : AutomaticActionState;
  approvalMatrixType: ApprovalMatrixTypeState;
  userHierarchy: UserHierarchyState
}

// Create middlewares
const middlewares: Middleware[] = [];

// Saga
const sagaMiddleware = createSagaMiddleware();
middlewares.push(sagaMiddleware);

// SignalR
middlewares.push(signalRMiddleware);

const bindMiddleware = (...middleware: Middleware[]) => {
  if (import.meta.env.DEV) {
    const devToolsCompose = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    return devToolsCompose(applyMiddleware(...middleware));
  }

  return applyMiddleware(...middleware);
};

// Create store
const store = createStore(RootReducer, bindMiddleware(...middlewares));

// Set store reference for utils.ts (breaks circular dependency)
setStoreRef(store);

// Run saga
sagaMiddleware.run(RootSaga);

export { store };

